// controllers/leaveController.js
import apiService from '../services/apiService';
import Leave from '../models/Leave';

export const leaveController = {
  /**
   * Get all leaves with filters
   * @param {Object} filters - { page, limit, status, leaveType, employeeId, startDate, endDate }
   * @returns {Promise<Object>} - { success, data, total, pagination }
   */
  getAll: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? `leaves?${queryParams}` : 'leaves';
      const response = await apiService.get(endpoint);
      
      const items = response.data?.map(item => Leave.fromJSON(item)) || [];
      
      return { 
        success: true, 
        data: items, 
        total: response.total || items.length,
        pagination: response.pagination || null
      };
    } catch (error) {
      console.error('Error fetching leaves:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message || 'Failed to fetch leave records' 
      };
    }
  },

  /**
   * Get single leave by ID
   * @param {string} id - Leave ID
   * @returns {Promise<Object>} - { success, data }
   */
  getById: async (id) => {
    try {
      const response = await apiService.get(`leaves/${id}`);
      return { 
        success: true, 
        data: Leave.fromJSON(response.data) 
      };
    } catch (error) {
      console.error('Error fetching leave:', error);
      return { 
        success: false, 
        data: null, 
        message: error.message 
      };
    }
  },

  /**
   * Get leaves by employee ID
   * @param {string} employeeId - Employee ID
   * @param {Object} filters - Additional filters
   * @returns {Promise<Object>} - { success, data, total }
   */
  getByEmployeeId: async (employeeId, filters = {}) => {
    try {
      const queryParams = new URLSearchParams({ ...filters, employeeId }).toString();
      const endpoint = `leaves?${queryParams}`;
      const response = await apiService.get(endpoint);
      
      const items = response.data?.map(item => Leave.fromJSON(item)) || [];
      
      return { 
        success: true, 
        data: items, 
        total: response.total || items.length 
      };
    } catch (error) {
      console.error('Error fetching employee leaves:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message 
      };
    }
  },

  /**
   * Create/Apply for new leave
   * @param {Object} data - Leave data
   * @returns {Promise<Object>} - { success, data, message }
   */
  create: async (data) => {
    try {
      // Backend expects 'numberOfDays' not 'days'
      // Backend will auto-assign 'employee' from authenticated user
      
      const leaveData = {
        leaveType: data.leaveType,
        startDate: data.startDate,
        endDate: data.endDate,
        numberOfDays: data.numberOfDays || data.days, // Support both field names
        reason: data.reason,
        halfDay: data.halfDay || false
      };
      
      // Add optional fields if present
      if (data.contactPhone) leaveData.contactPhone = data.contactPhone;
      if (data.emergencyContact) leaveData.emergencyContact = data.emergencyContact;
      if (data.emergencyPhone) leaveData.emergencyPhone = data.emergencyPhone;
      
      console.log('Sending leave data to API:', leaveData);
      
      const response = await apiService.post('leaves', leaveData);
      
      return { 
        success: true, 
        data: Leave.fromJSON(response.data), 
        message: response.message || 'Leave application submitted successfully' 
      };
    } catch (error) {
      console.error('Error creating leave:', error);
      
      // Parse error message
      let errorMessage = error.message || 'Failed to submit leave application';
      let errors = [];
      
      // Check if error has validation errors from backend
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
        errors = [errorMessage];
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
        errors = [errorMessage];
      } else if (error.response?.data?.errors) {
        // Handle mongoose validation errors
        const validationErrors = error.response.data.errors;
        if (typeof validationErrors === 'object') {
          errors = Object.values(validationErrors).map(err => err.message || err);
        } else if (Array.isArray(validationErrors)) {
          errors = validationErrors;
        } else {
          errors = [validationErrors];
        }
      } else {
        errors = [errorMessage];
      }
      
      return { 
        success: false, 
        message: errorMessage,
        errors: errors
      };
    }
  },

  /**
   * Update leave
   * @param {string} id - Leave ID
   * @param {Object} data - Updated data
   * @returns {Promise<Object>} - { success, data, message }
   */
  update: async (id, data) => {
    try {
      const leave = new Leave({ ...data, id });
      const validation = leave.validate();
      
      if (!validation.isValid) {
        return { 
          success: false, 
          errors: validation.errors 
        };
      }
      
      // Recalculate days
      if (leave.startDate && leave.endDate) {
        leave.days = leave.calculateDays();
      }
      
      const response = await apiService.put(`leaves/${id}`, leave.toJSON());
      
      return { 
        success: true, 
        data: Leave.fromJSON(response.data), 
        message: response.message || 'Leave updated successfully' 
      };
    } catch (error) {
      console.error('Error updating leave:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to update leave' 
      };
    }
  },

  /**
   * Delete leave
   * @param {string} id - Leave ID
   * @returns {Promise<Object>} - { success, message }
   */
  delete: async (id) => {
    try {
      const response = await apiService.delete(`leaves/${id}`);
      
      return { 
        success: true, 
        message: response.message || 'Leave deleted successfully' 
      };
    } catch (error) {
      console.error('Error deleting leave:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to delete leave' 
      };
    }
  },

  /**
   * Approve leave (Admin/Manager only)
   * @param {string} id - Leave ID
   * @param {Object} data - { approvedBy, remarks }
   * @returns {Promise<Object>} - { success, data, message }
   */
  approve: async (id, data = {}) => {
    try {
      const response = await apiService.patch(`leaves/${id}/approve`, data);
      
      return { 
        success: true, 
        data: Leave.fromJSON(response.data), 
        message: response.message || 'Leave approved successfully' 
      };
    } catch (error) {
      console.error('Error approving leave:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to approve leave' 
      };
    }
  },

  /**
   * Reject leave (Admin/Manager only)
   * @param {string} id - Leave ID
   * @param {Object} data - { rejectionReason }
   * @returns {Promise<Object>} - { success, data, message }
   */
  reject: async (id, data = {}) => {
    try {
      if (!data.rejectionReason || !data.rejectionReason.trim()) {
        return { 
          success: false, 
          message: 'Rejection reason is required' 
        };
      }
      
      const response = await apiService.patch(`leaves/${id}/reject`, data);
      
      return { 
        success: true, 
        data: Leave.fromJSON(response.data), 
        message: response.message || 'Leave rejected' 
      };
    } catch (error) {
      console.error('Error rejecting leave:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to reject leave' 
      };
    }
  },

  /**
   * Cancel leave (Employee only - for pending leaves)
   * @param {string} id - Leave ID
   * @returns {Promise<Object>} - { success, data, message }
   */
  cancel: async (id) => {
    try {
      const response = await apiService.patch(`leaves/${id}/cancel`);
      
      return { 
        success: true, 
        data: Leave.fromJSON(response.data), 
        message: response.message || 'Leave cancelled successfully' 
      };
    } catch (error) {
      console.error('Error cancelling leave:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to cancel leave' 
      };
    }
  },

  /**
   * Get leave balance
   * @param {string} employeeId - Employee ID (optional, uses current user if not provided)
   * @returns {Promise<Object>} - { success, data }
   */
  getBalance: async (employeeId = null) => {
    try {
      const endpoint = employeeId 
        ? `leaves/balance?employeeId=${employeeId}` 
        : 'leaves/balance';
      
      const response = await apiService.get(endpoint);
      
      return { 
        success: true, 
        data: response.data || {
          casual: 0,
          sick: 0,
          paid: 0,
          unpaid: 0,
          total: 0
        }
      };
    } catch (error) {
      console.error('Error fetching leave balance:', error);
      return { 
        success: false, 
        data: {
          casual: 0,
          sick: 0,
          paid: 0,
          unpaid: 0,
          total: 0
        },
        message: error.message 
      };
    }
  },

  /**
   * Get leave statistics
   * @param {Object} filters - { startDate, endDate, department, employeeId }
   * @returns {Promise<Object>} - { success, data }
   */
  getStatistics: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams 
        ? `leaves/statistics?${queryParams}` 
        : 'leaves/statistics';
      
      const response = await apiService.get(endpoint);
      
      return { 
        success: true, 
        data: response.data 
      };
    } catch (error) {
      console.error('Error fetching statistics:', error);
      return { 
        success: false, 
        data: null, 
        message: error.message 
      };
    }
  },

  /**
   * Get pending leaves (for approvers)
   * @param {Object} filters - Additional filters
   * @returns {Promise<Object>} - { success, data, total }
   */
  getPending: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams({ 
        ...filters, 
        status: 'Pending' 
      }).toString();
      
      const endpoint = `leaves?${queryParams}`;
      const response = await apiService.get(endpoint);
      
      const items = response.data?.map(item => Leave.fromJSON(item)) || [];
      
      return { 
        success: true, 
        data: items, 
        total: response.total || items.length 
      };
    } catch (error) {
      console.error('Error fetching pending leaves:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message 
      };
    }
  },

  /**
   * Get today's leaves
   * @returns {Promise<Object>} - { success, data, total }
   */
  getTodayLeaves: async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await apiService.get(`leaves?date=${today}&status=Approved`);
      
      const items = response.data?.map(item => Leave.fromJSON(item)) || [];
      
      return { 
        success: true, 
        data: items, 
        total: items.length 
      };
    } catch (error) {
      console.error('Error fetching today leaves:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message 
      };
    }
  },

  /**
   * Export leaves
   * @param {Object} filters - Export filters
   * @param {string} format - 'csv' | 'excel' | 'pdf'
   * @returns {Promise<Object>} - { success, data }
   */
  export: async (filters = {}, format = 'csv') => {
    try {
      const queryParams = new URLSearchParams({ ...filters, format }).toString();
      const response = await apiService.get(`leaves/export?${queryParams}`);
      
      return { 
        success: true, 
        data: response.data 
      };
    } catch (error) {
      console.error('Error exporting leaves:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to export leaves' 
      };
    }
  },

  /**
   * Upload attachment
   * @param {string} leaveId - Leave ID
   * @param {File} file - File to upload
   * @returns {Promise<Object>} - { success, data, message }
   */
  uploadAttachment: async (leaveId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await apiService.postFormData(
        `leaves/${leaveId}/upload`, 
        formData
      );
      
      return { 
        success: true, 
        data: response.data, 
        message: response.message || 'Attachment uploaded successfully' 
      };
    } catch (error) {
      console.error('Error uploading attachment:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to upload attachment' 
      };
    }
  },

  /**
   * Check leave availability
   * @param {Object} data - { leaveType, startDate, endDate }
   * @returns {Promise<Object>} - { success, available, balance, message }
   */
  checkAvailability: async (data) => {
    try {
      const response = await apiService.post('leaves/check-availability', data);
      
      return { 
        success: true, 
        available: response.data.available,
        balance: response.data.balance,
        message: response.message 
      };
    } catch (error) {
      console.error('Error checking availability:', error);
      return { 
        success: false, 
        available: false,
        message: error.message 
      };
    }
  }
};

export default leaveController;